import React, { useMemo } from "react";

/**
 * Tokenize a message string into plain-text and @mention segments.
 *
 * Rules (Slack-compatible):
 *   - A mention is @word where word = [a-zA-Z0-9_.-]+
 *   - Consecutive mentions are each their own token
 *   - Everything else is a plain-text token
 *
 * Returns an array of { type: "text"|"mention", value: string }
 *
 * Pure function — no side-effects, safe to memoize.
 */
export function parseMentions(text) {
  if (!text) return [];

  const MENTION_RE = /@([a-zA-Z0-9_.'-]+)/g;
  const tokens = [];
  let lastIndex = 0;
  let match;

  while ((match = MENTION_RE.exec(text)) !== null) {
    // Plain text before this mention
    if (match.index > lastIndex) {
      tokens.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    tokens.push({ type: "mention", value: match[0] }); // e.g. "@john"
    lastIndex = match.index + match[0].length;
  }

  // Remaining plain text after last mention
  if (lastIndex < text.length) {
    tokens.push({ type: "text", value: text.slice(lastIndex) });
  }

  return tokens;
}

/**
 * MentionText
 *
 * Renders a message string with @mentions highlighted as Slack-style pills.
 *
 * Props:
 *   text        {string}  - Raw message content
 *   currentUser {string}  - Current user's name or username (for self-mention highlight)
 *   className   {string}  - Extra classes on the wrapper <span>
 *
 * XSS-safe: renders React elements only, never dangerouslySetInnerHTML.
 * Theme-aware: uses CSS variables / Tailwind tokens from the design system.
 */
export default function MentionText({ text, currentUser, className = "" }) {
  const tokens = useMemo(() => parseMentions(text), [text]);

  if (!tokens.length) return <span className={className}>{text}</span>;

  return (
    <span className={className}>
      {tokens.map((token, i) => {
        if (token.type === "text") {
          return <React.Fragment key={i}>{token.value}</React.Fragment>;
        }

        // Determine if this mention is the current user (self-mention → stronger highlight)
        const mentionName = token.value.slice(1).toLowerCase(); // strip "@"
        const isSelf =
          currentUser &&
          (currentUser.toLowerCase() === mentionName ||
            currentUser.toLowerCase().replace(/\s+/g, "") === mentionName);

        return (
          <span
            key={i}
            className={
              isSelf
                ? "mention mention--self"
                : "mention"
            }
          >
            {token.value}
          </span>
        );
      })}
    </span>
  );
}
